/**
 * /pipeline-facility/jobs/:id — one job (GET / PATCH / DELETE
 * /pipeline-facility/jobs/:id, fieldiq-api#1200, PF-2).
 *
 * A full page with tabs (`?tab=`, so Back and a shared link keep the place):
 * Overview (details, status actions, edit, delete), Crew, Equipment (owned
 * units), Rent-ins and Daily logs. Forms open inline, never as a modal; a
 * status move, a delete and a removal ask in-app first.
 *
 * Writes are PF_DISPATCH. The status buttons are the moves PF_JOB_TRANSITIONS
 * allows from where the job is — a mirror for which buttons to draw; the API
 * decides. Cancelling asks for a reason (the API requires one). Delete is
 * offered on a draft or scheduled job; a job with logs is refused
 * (job_has_logs) and the refusal says to cancel instead.
 *
 * Rent-in cost (rate, billable, PO) is shown only to roles the API sends it to.
 *
 * PF-6 (fieldiq-api#1236), for the AI review roles only: the Daily logs tab
 * drafts a day's summary (and opens the draft); the Tickets tab runs the job
 * preflight in place.
 */
import React, { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Activity, ClipboardList, Pencil, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import {
  useDeletePfJob,
  usePfCustomers,
  usePfEquipmentOptions,
  usePfJob,
  usePfMeta,
  usePfUsers,
  useUpdatePfJob,
} from '../../hooks/usePipelineFacility';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  canDispatchPf,
  canSeeRentInCost,
  canWorkPf,
  pfDay,
  pfJobDeletable,
  pfJobLoggable,
  pfJobStatusSkin,
  pfNextStatuses,
  pfRefusal,
} from '../../lib/pipeline-facility';
import { DH_BAND_SECONDARY } from '../downhole/DownholeBits';
import { useBackToList, useOpenRecord } from '../downhole/recordPage';
import { JobForm } from './JobForm';
import { chainageRange } from './JobsScreen';
import { CrewSection, EquipmentSection, LogsSection, RentInsSection } from './JobSections';
import { PfJobDigsSection, PfJobWeldsSection } from './digs/JobDigSections';
import { PfJobTicketsSection, PfRentInBillingPanel } from './tickets/JobTicketSections';
import { PfJobInvoicesSection } from './invoices/InvoiceBits';
import { canReadPfTickets, canWritePfTickets } from '../../lib/pf-tickets';
import { canUsePfAi } from '../../lib/pf-ai';
import { PfDailySummaryLauncher, PfJobPreflight } from './ai/PfAiBits';
import {
  isNotFound,
  PF_EYEBROW,
  PF_INPUT,
  PF_PRIMARY,
  PF_SECONDARY,
  PfBadge,
  PfDialog,
  PfEmpty,
  PfField,
  PfFormError,
  PfLoading,
  PfPanel,
  PfSpec,
} from './PfBits';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'crew', label: 'Crew' },
  { key: 'equipment', label: 'Equipment' },
  { key: 'rent-ins', label: 'Rent-ins' },
  { key: 'logs', label: 'Daily logs' },
  { key: 'digs', label: 'Digs' },
  { key: 'welds', label: 'Welds' },
  { key: 'tickets', label: 'Tickets' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/** The button for a move, by where it goes (and, for draft, where it comes from). */
const MOVE_LABEL: Record<string, string> = {
  draft: 'Back to draft',
  scheduled: 'Schedule',
  in_progress: 'Start work',
  completed: 'Complete',
  cancelled: 'Cancel job',
};
const MOVE_BODY: Record<string, string> = {
  draft: 'Moves the job back to draft. The crew cannot log work on a draft.',
  scheduled: 'Schedules the job. The crew can log work on it from today.',
  in_progress: 'Marks the job in progress. The first daily log does this on its own.',
  completed: 'Completes the job. A completed job takes no more logs or changes.',
  cancelled: 'Cancels the job. A cancelled job takes no more logs or changes.',
};

export default function PfJobDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const back = useBackToList('/pipeline-facility/jobs');
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as TabKey | null;
  const tab: TabKey = TABS.some((t) => t.key === tabParam) ? (tabParam as TabKey) : 'overview';
  const pickTab = (key: TabKey) => {
    const p = new URLSearchParams(searchParams);
    if (key === 'overview') p.delete('tab');
    else p.set('tab', key);
    setSearchParams(p, { replace: true });
  };

  const { user } = useAuth();
  const role = user?.role;
  const canDispatch = canDispatchPf(role);
  const query = usePfJob(id);
  const catalog = usePfMeta().data?.data ?? null;
  const customers = usePfCustomers(canDispatch);
  const users = usePfUsers(canDispatch);
  const equipment = usePfEquipmentOptions(canDispatch);
  const update = useUpdatePfJob();
  const del = useDeletePfJob();
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [move, setMove] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [moveError, setMoveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const job = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const u = meta?.units ?? null;
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const open = !!job && job.status !== 'completed' && job.status !== 'cancelled';
  const canWrite = canDispatch && open;
  const moves = canDispatch ? pfNextStatuses(job?.status) : [];

  const doMove = () => {
    if (!job || !move) return;
    setMoveError(null);
    if (move === 'cancelled' && !cancelReason.trim()) return setMoveError('Say why the job is cancelled.');
    update.mutate(
      { id: job.id, body: move === 'cancelled' ? { status: move, cancel_reason: cancelReason.trim() } : { status: move } },
      {
        onSuccess: () => {
          toast.success(`Job ${pfJobStatusSkin(move).label.toLowerCase()}`);
          setMove(null);
          setCancelReason('');
        },
        onError: (err) => setMoveError(pfRefusal(err, 'The status could not be changed.')),
      },
    );
  };

  return (
    <div data-testid="pf-job-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={job?.moduleLabel ? `${PF_EYEBROW} · ${job.moduleLabel}` : PF_EYEBROW}
        title={job?.jobNumber || (query.isLoading ? 'Job' : '—')}
        description={job ? `${job.customerName ?? '—'} · ${job.siteName ?? '—'}` : undefined}
        onBack={back}
        backLabel="Back"
        actions={
          job && canDispatch ? (
            <div className="flex flex-wrap gap-2" data-testid="pf-job-actions">
              {moves.map((to) => (
                <button
                  key={to}
                  type="button"
                  className={to === moves[0] && to !== 'cancelled' ? 'fi-action fi-action--standard fi-action--primary' : DH_BAND_SECONDARY}
                  onClick={() => { setMoveError(null); setCancelReason(''); setMove(to); }}
                >
                  {MOVE_LABEL[to] ?? pfJobStatusSkin(to).label}
                </button>
              ))}
              {open && (
                <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setEditError(null); setEditing(true); pickTab('overview'); }} disabled={editing}>
                  <Pencil className="size-4" aria-hidden="true" />
                  Edit
                </button>
              )}
              {pfJobDeletable(job.status) && (
                <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              )}
            </div>
          ) : undefined
        }
        stats={[
          { icon: <Activity className="size-4" aria-hidden="true" />, label: 'Status', tone: 'neutral', loading: query.isLoading, value: job ? pfJobStatusSkin(job.status).label : '—' },
          { icon: <Users className="size-4" aria-hidden="true" />, label: 'Crew', tone: 'neutral', loading: query.isLoading, value: job ? displayValue(job.crew.length) : '—' },
          { icon: <ClipboardList className="size-4" aria-hidden="true" />, label: 'Daily logs', sub: job?.lastLogDate ? `Last ${pfDay(job.lastLogDate)}` : undefined, tone: 'neutral', loading: query.isLoading, value: job ? displayValue(job.dailyLogCount ?? undefined) : '—' },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <PfLoading label="Loading job" />
          ) : notFound || (!failure && !job) ? (
            <PfPanel eyebrow="Job" title="Job">
              <PfEmpty>This job was not found. It may have been deleted, or you may not be on its crew.</PfEmpty>
            </PfPanel>
          ) : failure || !job ? (
            <PfPanel eyebrow="Job" title="Job">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this job" />
            </PfPanel>
          ) : (
            <>
              <div role="tablist" aria-label="Job sections" className="flex gap-5 overflow-x-auto border-b border-fieldiq-border">
                {TABS.filter((t) => t.key !== 'tickets' || canReadPfTickets(role)).map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={tab === t.key}
                    onClick={() => pickTab(t.key)}
                    className={`whitespace-nowrap border-b-2 px-1 pb-2 text-sm transition ${
                      tab === t.key ? 'border-fi-amber font-extrabold text-fieldiq-text' : 'border-transparent font-bold text-fieldiq-muted hover:text-fieldiq-text'
                    }`}
                  >
                    {t.label}
                    {t.key === 'crew' ? ` (${job.crew.length})` : t.key === 'equipment' ? ` (${job.equipment.length})` : t.key === 'rent-ins' ? ` (${job.rentIns.length})` : ''}
                  </button>
                ))}
              </div>

              {tab === 'overview' && (
                <>
                  {!open && (
                    <p className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text" data-testid="pf-job-closed">
                      This job is {pfJobStatusSkin(job.status).label.toLowerCase()}. It takes no more logs or changes.
                      {job.cancelReason ? ` Reason: ${job.cancelReason}` : ''}
                    </p>
                  )}
                  <PfPanel eyebrow={job.moduleLabel ?? 'Job'} title="Details" testId="pf-job-details">
                    <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                      <PfSpec label="Status" value={<PfBadge skin={pfJobStatusSkin(job.status)} value={job.status} />} />
                      <PfSpec label="Customer" value={job.customerName || '—'} />
                      <PfSpec label="Site" value={job.siteName || '—'} />
                      <PfSpec label="Segment" value={job.segmentLabel || '—'} />
                      <PfSpec label={`Chainage${u?.chainage ? ` (${u.chainage})` : ''}`} value={chainageRange(job.fromChainage, job.toChainage, null)} mono />
                      <PfSpec label="Location" value={job.locationName || '—'} />
                      <PfSpec label="Code" value={job.codeLabel || job.code || '—'} />
                      <PfSpec label="Tickets" value={catalog?.ticketModes.find((t) => t.value === job.ticketMode)?.label ?? job.ticketMode ?? '—'} />
                      <PfSpec label="Supervisor" value={job.supervisorName || '—'} />
                      <PfSpec label="Scheduled" value={`${pfDay(job.scheduledStart)} → ${pfDay(job.scheduledEnd)}`} />
                      <PfSpec label="PO number" value={job.poNumber || '—'} mono />
                      <PfSpec label="AFE number" value={job.afeNumber || '—'} mono />
                      <PfSpec label="Cost code" value={job.costCode || '—'} mono />
                      <PfSpec label="Currency" value={job.currency || '—'} />
                      <PfSpec label="Owned units" value={displayValue(job.equipment.length)} />
                      <PfSpec label="Rent-ins" value={displayValue(job.rentIns.length)} />
                      {/* Only a job booked from a quote has one (api#1232); a payload without the key draws nothing. */}
                      {job.sourceQuote?.id && (
                        <PfSpec
                          label="Quote"
                          value={
                            <Link to={`/quotes/${job.sourceQuote.id}`} data-testid="pf-job-source-quote" className="font-bold text-fieldiq-text underline">
                              From quote {job.sourceQuote.number || '—'}
                            </Link>
                          }
                        />
                      )}
                    </dl>
                    {/* pre-wrap: a job booked from a quote carries its scope one line per quote line (api create-pf-job). */}
                    {job.scope && <p data-testid="pf-job-scope" className="whitespace-pre-wrap border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text"><span className="font-bold">Scope: </span>{job.scope}</p>}
                    {job.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{job.notes}</p>}
                  </PfPanel>

                  {editing && canWrite && (
                    <PfPanel eyebrow="Edit" title="Edit job" testId="pf-job-edit">
                      <JobForm
                        job={job}
                        catalog={catalog}
                        meta={meta}
                        customers={customers.data ?? []}
                        users={users.data ?? []}
                        pending={update.isPending}
                        error={editError}
                        submitLabel="Save job"
                        onCancel={() => setEditing(false)}
                        onSubmit={(body) =>
                          update.mutate(
                            { id: job.id, body },
                            {
                              onSuccess: () => {
                                toast.success('Job saved');
                                setEditing(false);
                              },
                              onError: (err) => setEditError(pfRefusal(err, 'The job could not be saved.')),
                            },
                          )
                        }
                      />
                    </PfPanel>
                  )}
                </>
              )}

              {tab === 'crew' && <CrewSection job={job} catalog={catalog} users={users.data ?? []} canWrite={canWrite} />}
              {tab === 'equipment' && <EquipmentSection job={job} equipment={equipment.data ?? []} canWrite={canWrite} />}
              {tab === 'rent-ins' && (
                <RentInsSection
                  job={job}
                  catalog={catalog}
                  meta={meta}
                  equipment={equipment.data ?? []}
                  canWrite={canWrite}
                  canSeeCost={canSeeRentInCost(role)}
                />
              )}
              {tab === 'rent-ins' && canReadPfTickets(role) && (
                <PfRentInBillingPanel job={job} rentIns={job.rentIns ?? []} canWrite={canWritePfTickets(role)} />
              )}
              {tab === 'digs' && <PfJobDigsSection jobId={job.id} canPlan={canWrite} />}
              {tab === 'tickets' && (canReadPfTickets(role)
                ? <><PfJobTicketsSection job={job} canWrite={canWritePfTickets(role)} today={meta?.today ?? null} />{canUsePfAi(role) && <PfJobPreflight jobId={job.id} canReview />}<PfJobInvoicesSection jobId={job.id} /></>
                : <PfPanel eyebrow="Field tickets" title="Field tickets"><PfEmpty>Field tickets carry prices, so only the office, supervisors and dispatch see them.</PfEmpty></PfPanel>)}
              {tab === 'welds' && <PfJobWeldsSection jobId={job.id} canRecord={canWorkPf(role) && open} />}
              {tab === 'logs' && canUsePfAi(role) && <PfDailySummaryLauncher jobId={job.id} />}
              {tab === 'logs' && (
                <LogsSection
                  job={job}
                  canLog={canWorkPf(role) && pfJobLoggable(job.status)}
                  onNew={() => openRecord(`/pipeline-facility/jobs/${job.id}/daily-logs/new`)}
                  onOpen={(l) => openRecord(`/pipeline-facility/daily-logs/${l.id}`)}
                />
              )}
            </>
          )}
        </div>
      </section>

      {job && canDispatch && (
        <>
          <PfDialog
            open={!!move}
            title={move ? MOVE_LABEL[move] ?? 'Change status' : 'Change status'}
            onClose={() => setMove(null)}
            footer={
              <>
                <button type="button" className={PF_SECONDARY} onClick={() => setMove(null)} disabled={update.isPending}>
                  Not now
                </button>
                <button type="button" className={PF_PRIMARY} onClick={doMove} disabled={update.isPending}>
                  {update.isPending ? 'Saving…' : move ? MOVE_LABEL[move] ?? 'Confirm' : 'Confirm'}
                </button>
              </>
            }
          >
            <PfFormError message={moveError} />
            <p className="text-sm text-fieldiq-text">{move ? MOVE_BODY[move] : ''}</p>
            {move === 'cancelled' && (
              <div className="mt-3">
                <PfField label="Reason">
                  <textarea className={`${PF_INPUT} h-20 py-2`} aria-label="Cancel reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
                </PfField>
              </div>
            )}
          </PfDialog>
          <PfDialog
            open={deleting}
            title="Delete job"
            onClose={() => setDeleting(false)}
            footer={
              <>
                <button type="button" className={PF_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>
                  Keep job
                </button>
                <button
                  type="button"
                  className={PF_PRIMARY}
                  disabled={del.isPending}
                  onClick={() =>
                    del.mutate(job.id, {
                      onSuccess: () => {
                        toast.success('Job deleted');
                        navigate('/pipeline-facility/jobs', { replace: true });
                      },
                      onError: (err) => setDeleteError(pfRefusal(err, 'The job could not be deleted.')),
                    })
                  }
                >
                  {del.isPending ? 'Deleting…' : 'Delete job'}
                </button>
              </>
            }
          >
            <PfFormError message={deleteError} />
            <p className="text-sm text-fieldiq-text">Removes {job.jobNumber} from the register. A job with daily logs cannot be deleted — cancel it instead.</p>
          </PfDialog>
        </>
      )}
    </div>
  );
}
